
import { deburr } from 'lodash'
import Vue from 'vue'
import MenuItem from '@/Models/Static/MenuItem'
import MenuItemType from '@/Models/Static/MenuItemType'

const MINIMUM_SEARCH_CHARS = 2

const splitIntoSearchWords = v => v
  .trim()
  .toLowerCase()
  .split(/[\s,-]+/)
  .filter(word => word.length >= MINIMUM_SEARCH_CHARS)
  .map(word => deburr(word))

const startsWith = (haystack, needles) => needles
  .every(needle => haystack.some(word => word.startsWith(needle)))

const state = () => ({
  /**
   * This variable contains all the catalog tables in JSON format.
   * @type Object
   */
  data: {},

  /**
   * This variable contains all the searchable items.
   * @type Array
   */
  searchable: [],

  /**
   * The search string.
   * @type String
   */
  needle: '',

  /**
   * Group tab value.
   * @type Number
   */
  groupTab: 0,

  /**
   * Categories tab values.
   * @type Object
   */
  categoryTabs: {},
})

const getters = {
  _get: state => (model, id) => state.data?.[model]?.find(o => o.id == id),

  group: (state, getters) => id => getters._get('MenuGroup', id),
  groups: state => state.data?.MenuGroup
    ?.filter(group => !! group.active)
    .sortByNumber('order')
    ?? []
  ,

  category: (state, getters) => id => getters._get('MenuCategory', id),
  categories: state => group_id => state.data?.MenuCategory
    ?.filter(category => !! category.active && category.group_id == group_id)
    .sortByNumber('order')
    ?? []
  ,

  item: (state, getters) => id => getters._get('MenuItem', id),
  categoryItems: (state, getters) => category_id => state.data?.MenuItem
    ?.filter(item => item.category_id == category_id && !! item.active && MenuItem.enabled(item))
    .filter(item => item.type != MenuItemType.LINKED || getters.linkedItems(item.id).length > 0) // REV-5046
    .sortByNumber('order')
    ?? []
  ,
  linkedItems: (state, getters) => item_id => state.data?.LinkedItemPivot
    ?.filter(pivot => pivot.main_item_id == item_id && !! pivot.active)
    .sortByNumber('order')
    .map(pivot => ! pivot.dish_order_id
      ? getters.item(pivot.item_id)
      : { ...getters.item(pivot.item_id), dish_order_id: pivot.dish_order_id })
    .filter(item => MenuItem.enabled(item))
    ?? []
  ,

  dishOrder: (state, getters) => id => getters._get('DishOrder', id),
  dishOrders: state => [...state.data?.DishOrder ?? []].sortByNumber('order'),
  activeDishOrders: (state, getters) => getters.dishOrders.filter(o => !! o.active),
  dishOrderByOrder: (state, getters) => order => state.data?.DishOrder?.find(o => o.order == order),
  itemDishOrder: (state, getters) => item => getters.dishOrder(item.dish_order_id ?? getters.category(item.category_id).dish_order_id),

  sellingFormat: (state, getters) => id => getters._get('SellingFormat', id),
  sellingFormatPivot: (state, getters) => payload => typeof payload === 'number'
    ? getters._get('ItemSellingFormatPivot', payload)
    : state.data?.ItemSellingFormatPivot
      ?.find(o => o.item_id == payload.item_id && o.format_id == payload.format_id)
  ,
  sellingFormatItems: (state, getters) => item_id => state.data?.ItemSellingFormatPivot
    ?.filter(pivot => pivot.item_id == item_id)
    .sortByNumber('order')
    .map(pivot => getters.sellingFormat(pivot.format_id))
    .filter(sellingFormat => !! sellingFormat.active)
    ?? []
  ,
  sellingFormatCombinations: (state, getters) => group_id => state.data?.CombinationGroupMenuItem
    ?.filter(pivot => pivot.combination_group_id == group_id)
    .sortByNumber('order')
    ?? []
  ,

  needle: state => {
    return state.needle.trim()
  },
  searching: (state, getters) => {
    return getters.needle.length >= MINIMUM_SEARCH_CHARS
  },
  searchItems: (state, getters) => {
    if (getters.needle.length < MINIMUM_SEARCH_CHARS) {
      return []
    }
    const needles = splitIntoSearchWords(state.needle)
    return state.searchable.filter(item => startsWith(item.haystack, needles))
  },

  ///////////////
  // MODIFIERS //
  ///////////////

  // one MenuItem belongsTo ModifierGroup or belongsTo MenuCategory that belongsTo ModifierGroup
  _getModifierGroupId: (state, getters) => item => item.modifier_group_id || getters.category(item.category_id)?.modifier_group_id,

  // one MenuItem belongsTo ModifierCategory or belongsTo MenuCategory that belongsTo ModifierCategory
  _modifierCategoryId: (state, getters) => item => item.modifier_category_id || getters.category(item.category_id)?.modifier_category_id,

  modifierGroup: (state, getters) => item => getters._get('ModifierGroup', getters._getModifierGroupId(item)),
  _modifierCategory: (state, getters) => id => getters._get('ModifierCategory', id),
  modifierGroupCategories: (state, getters) => group_id => state.data?.ModifierPivot
    ?.filter(pivot => pivot.group_id == group_id)
    .sortByNumber('order')
    .map(pivot => getters._modifierCategory(pivot.category_id))
    .filter(category => !! category.active)
    ?? []
  ,
  _modifierCategories: (state, getters) => item => [
         getters._modifierCategory(getters._modifierCategoryId(item)),
      ...getters.modifierGroupCategories(getters._getModifierGroupId(item)),
    ].filter(o => !! o)
  ,
  visibleModifierCategories: (state, getters) => item => getters._modifierCategories(item).filter(o => ! o.shouldHide),
  hiddenModifierCategories: (state, getters) => item => getters._modifierCategories(item).filter(o => !! o.shouldHide),
  hasVisibleModifiers: (state, getters) => item => getters.visibleModifierCategories(item).length > 0,
  showModifiers: (state, getters) => item => getters.hasVisibleModifiers(item) && ! getters.modifierGroup(item)?.dont_show_automatically,
  modifiers: state => category_id => state.data?.Modifier
    ?.filter(modifier => modifier.category_id == category_id && !! modifier.active)
    .sortByNumber('order')
    ?? []
  ,

  ///////////
  // MENUS //
  ///////////

  menuCategories: state => item => state.data?.MenuMenuCategory
    ?.filter(category => category.item_id == item.id)
    .sortByNumber('order')
    ?? []
  ,
  menuItems: (state, getters) => category_id => state.data?.MenuMenuPivot
    ?.filter(pivot => pivot.category_id == category_id && !! pivot.active)
    .sortByNumber('order')
    .map(pivot => ({
      ...getters.item(pivot.item_id),
      menuPivot: {
        id: pivot.id,
        price: parseFloat(pivot.price),
        addModifiersPrice: !! pivot.addModifiersPrice,
        modifier_group_id: pivot.modifier_group_id,
      },
    }))
    .filter(item => MenuItem.enabled(item))
    ?? []
  ,
}

const mutations = {
  set (state, { model, data }) {
    Vue.set(state.data, model, data)
  },
  searchable (state, v) {
    state.searchable = v
  },
  needle (state, v) {
    state.needle = v
  },
  groupTab (state, v) {
    state.groupTab = v
  },
  categoryTab (state, v) {
    state.categoryTabs[state.groupTab] = v
  },
  unsetCategoryTabs (state) {
    state.categoryTabs = {}
  },
}

const actions = {
  reset ({ commit }) {
    commit('groupTab', 0)
    commit('unsetCategoryTabs')
  },
  searchable ({ state, getters, commit }) {
    const isActive = item => {
      if (! item.active || ! MenuItem.enabled(item)) {
        return false
      }
      // REV-5046
      if (item.type == MenuItemType.LINKED && ! getters.linkedItems(item.id).length) {
        return false
      }
      // REV-23029
      const category = getters.category(item.category_id)
      if (! category?.active) {
        return false
      }
      if (! getters.group(category.group_id)?.active) {
        return false
      }
      return true
    }
    const searchable = [
      ...state.data?.MenuItem?.filter(isActive),
      ...state.data?.LinkedItemPivot?.filter(pivot => !! pivot.active)
        .map(pivot => state.data?.MenuItem?.find(o => o.id == pivot.item_id && MenuItem.enabled(o)))
    ]
      .filter(item => !! item)
      .unique()
      .sortByString('name')
      .map(item => ({
        ...item,
        haystack: splitIntoSearchWords(item.name)
      }))

    commit('searchable', searchable)
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
